<script lang="ts">
  import { getLibxert } from '@/xert-wasm';
  import { getFocusDuration, SpecificityTypes, specificityValues } from '../../Common/constants';
  import Input from '@/lib/components/ui/input/input.svelte';
  import { Label } from '@/lib/components/ui/label';
  import * as Select from '@/lib/components/ui/select';
  import * as RadioGroup from '@/lib/components/ui/radio-group';
  import { cn } from '@/lib/utils';
  import { round, xssToFocusSpecificity, type XSSDetails } from '@/Util/utils';
  import { getContext } from 'svelte';
  import type { MyFitnessStores } from '../stores';

  export let xssDetails: XSSDetails<number>;

  export let xlss: number;
  export let xhss: number;
  export let xpss: number;
  let xss;

  $: spr = 'Mixed';
  $: sp = null;
  $: focus = 'Endurance';
  $: focusDuration = null;

  const myFitness = getContext<MyFitnessStores>('myFitness');
  const { userParams, trainingAdvice } = myFitness;

  let focusSelection = focus ? {
    label: focus,
    value: focus,
  } : undefined
  
  let sprSelection = spr ? {
    label: spr,
    value: spr,
  } : undefined

  let xssInputType = 'lhp';
  let focus_types = Object.keys($userParams.athletes).map((key) => $userParams.athletes[key].workoutname);

  // The following 4 values are used to update the XSS components reactively to changes in total XSS
  let xssComponentTotal;
  let xlssRatio;
  let xhssRatio;
  let xpssRatio;
  $: {
    updateXssFieldsFromXssDetails(xssDetails);
    onXssComponentChange();
  }

  let areXssValuesPossible = true;
  let showXssWarning = false;
  let xssErrorMessage = 'XSS values cannot be performed'

  async function updateXssFieldsFromXssDetails(xssDetails: XSSDetails<number>) {
    if (xssDetails != null) {
      xlss = round(xssDetails.xlss, 1);
      xhss = round(xssDetails.xhss, 1);
      xpss = round(xssDetails.xpss, 1);
      xss = round(xssDetails.xss, 1);
    }
  }

  async function updateXssComponentRatios() {
    xssComponentTotal = (xlss ?? 0) + (xhss ?? 0) + (xpss ?? 0);
    if (xssComponentTotal == 0) {
      const sig = $trainingAdvice.signature;
      const libxert = await getLibxert();
      const spCalculated = specificityValues[SpecificityTypes[spr]];
      const focusDurationCalculated = getFocusDuration($userParams.athletes, focus);
      // @ts-ignore
      const lhpxssRatios = libxert.power_alloc(sig.ftp, sig.atc, sig.pp, 1, focusDurationCalculated, spCalculated);
      xlssRatio = lhpxssRatios[0];
      xhssRatio = lhpxssRatios[1];
      xpssRatio = 1 - xlssRatio - xhssRatio;
    } else {
      xlssRatio = (xlss ?? 0) / xssComponentTotal;
      xhssRatio = (xhss ?? 0) / xssComponentTotal;
      xpssRatio = (xpss ?? 0) / xssComponentTotal;
    }
  }

  // scale XSS components (low, high, peak) proportionally to XSS total change
  function onXssTotalChange() {
    const xssValue = xss ?? 0;
    if (xlssRatio != null) {
      updateFocusAndSpecificity(xssValue * xlssRatio, xssValue * xhssRatio, xssValue * xpssRatio);
      xlss = round(xssValue * xlssRatio, 1);
      xhss = round(xssValue * xhssRatio, 1);
      xpss = round(xssValue * xpssRatio, 1);
    }
  }

  // update XSS components (low, high, peak) according to focus and specificity
  async function onFocusSpecificityChange(f: string, s: string) {
    focus = f ?? 'Endurance';
    spr = s ?? 'Mixed';

    const sig = $trainingAdvice.signature;
    const libxert = await getLibxert();
    const spCalculated = specificityValues[SpecificityTypes[spr]];
    const focusDurationCalculated = getFocusDuration($userParams.athletes, focus);
    // @ts-ignore
    const lhpxssRatios = libxert.power_alloc(sig.ftp, sig.atc, sig.pp, 1, focusDurationCalculated, spCalculated);
    sp = spCalculated;
    focusDuration = focusDurationCalculated;

    xlssRatio = lhpxssRatios[0];
    xhssRatio = lhpxssRatios[1];
    xpssRatio = 1 - xlssRatio - xhssRatio;
    xlss = round(lhpxssRatios[0] * xss, 1);
    xhss = round(lhpxssRatios[1] * xss, 1);
    xpss = round(xss - xlss - xhss, 1);
    areXssValuesPossible = true;
    showXssWarning = false;
  }

  async function updateFocusAndSpecificity(lxss: number, hxss: number, pxss: number) {
    const sig = $trainingAdvice.signature;
    const focusSpecificity = await xssToFocusSpecificity(sig, { xss: xss, xlss: lxss, xhss: hxss, xpss: pxss }, $userParams.athletes);
    const spCalculated = focusSpecificity.specificity;
    const f = focusSpecificity.sfd;
    areXssValuesPossible = xss == 0 || !(f == 0 || spCalculated <= 0 || spCalculated > 1 || hxss > 0 != pxss > 0);
    showXssWarning = areXssValuesPossible && spCalculated < 0.05;

    sp = spCalculated;
    focusDuration = f;

    spr = focusSpecificity.specificityRating;
    sprSelection = {
      value: spr,
      label: spr,
    };
    focus = focusSpecificity.focus;
    focusSelection = {
      value: focus,
      label: focus,
    };
  }

  // update total XSS, focus, and specificity to match XSS components (low, high, peak)
  async function onXssComponentChange() {
    const lxss = +(xlss ?? 0);
    const hxss = +(xhss ?? 0);
    const pxss = +(xpss ?? 0);
    xss = round(lxss + hxss + pxss, 1);
    updateFocusAndSpecificity(lxss, hxss, pxss);
    await updateXssComponentRatios();
  }
</script>

<div class='mt-4'>
  <Label class="font-semibold text-sm">Enter the XSS values with...</Label>
  <RadioGroup.Root bind:value={xssInputType} class="flex flex-row gap-3 flex-wrap">
    <Label class="flex flex-row items-center gap-2 relative border rounded-b-md rounded-t-md px-4 py-2 cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="lhp">
      <RadioGroup.Item class="" value="lhp" id="lhp" />
      <div class="font-semibold text-sm">Low/High/Peak</div>
    </Label>
    <Label class="flex flex-row items-center gap-2 relative border rounded-b-md rounded-t-md px-4 py-2 cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="focus">
      <RadioGroup.Item class="" value="focus" id="focus" />
      <div class="font-semibold text-sm">Focus/Specificity</div>
    </Label>
  </RadioGroup.Root>
</div>

{#if xssInputType == 'lhp'}
  <div>
    <div class="flex flex-row flex-wrap gap-3">
      <div class="min-w-[160px] flex-1">
        <Label class="font-medium text-sm text-xertlow">Low XSS</Label>
        <Input type="number" min="0" step="1" class="form-control input-circle-right" bind:value={xlss} placeholder="Low XSS..." on:input={onXssComponentChange} />
      </div>
      <div class="min-w-[160px] flex-1">
        <Label class="font-medium text-sm text-xerthigh">High XSS</Label>
        <Input type="number" min="0" step="1" class="form-control input-circle-right" bind:value={xhss} placeholder="High XSS..." on:input={onXssComponentChange} />
      </div>
      <div class="min-w-[160px] flex-1">
        <Label class="font-medium text-sm text-xertpeak">Peak XSS</Label>
        <Input type="number" min="0" step="1" class="form-control input-circle-right" bind:value={xpss} placeholder="Peak XSS..." on:input={onXssComponentChange} />
      </div>
    </div>
    {#if !areXssValuesPossible || showXssWarning}
      <p class={cn(showXssWarning ? 'text-yellow-600' : 'text-red-600')}>{xssErrorMessage}</p>
    {/if}
  </div>
  <div class="flex flex-row gap-3 flex-wrap">
    <div><span class="font-semibold text-sm">Total XSS: </span> {xss}</div>
    <div><span class="font-semibold text-sm">Focus: </span> {focus}</div>
    <div><span class="font-semibold text-sm">Specificity:</span> {spr}</div>
  </div>
{:else}
  <div>
    <div class="flex flex-row flex-wrap gap-3">
      <div class="min-w-[160px] flex-1">
        <Label class="font-semibold text-sm">XSS</Label>
        <Input type="number" min=0 step=1 class="form-control input-circle-right" bind:value={xss} on:input={onXssTotalChange}/>
      </div>
      <div class="min-w-[160px] flex-1">
        <Label class="font-semibold text-sm">Focus</Label>
        <Select.Root bind:selected={focusSelection} items={focus_types} onSelectedChange={(v) => onFocusSpecificityChange(v.value, spr)}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#if focus_types != null}
              {#each focus_types as value}
                <Select.Item {value} label={value}>{value}</Select.Item>
              {/each}
            {/if}
          </Select.Content>
        </Select.Root>
      </div>
      <div class="min-w-[160px] flex-1">
        <Label class="font-semibold text-sm">Specificity</Label>
        <Select.Root bind:selected={sprSelection} onSelectedChange={(v) => onFocusSpecificityChange(focus, v.value)}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            <Select.Item value="Pure" label="Pure">Pure</Select.Item>
            <Select.Item value="Mixed" label="Mixed">Mixed</Select.Item>
            <Select.Item value="Polar" label="Polar">Polar</Select.Item>
          </Select.Content>
        </Select.Root>
      </div>
    </div>
    {#if !areXssValuesPossible || showXssWarning}
      <p class={cn(showXssWarning ? 'text-yellow-600' : 'text-red-600')}>{xssErrorMessage}</p>
    {/if}
  </div>
  <div class="flex flex-row gap-3 flex-wrap">
    <div><span class="font-medium text-sm text-xertlow">Low XSS:</span> {xlss}</div>
    <div><span class="font-medium text-sm text-xerthigh">High XSS:</span> {xhss}</div>
    <div><span class="font-medium text-sm text-xertpeak">Peak XSS:</span> {xpss}</div>
  </div>
{/if}
